/**
 * CommitmentTable — stream-embedded list of Commitments (Normal Core §6.5.4).
 * Composes `DataTable`; mounts on each stream page's deliver stage (and
 * anywhere else a commitment list is needed) — deliberately NOT a new
 * top-level surface (congestion rule, C13).
 *
 * The Commitment `state` is a free domain string (the aggregate does not
 * constrain it), so the pill resolves through `commitmentStateIntent` — a
 * keyword mapping with a neutral fallback — rather than a status-token domain.
 *
 * Labels are props with canonical defaults so the C15 Lexicon (`vocab()`)
 * can be wired through at flip time without touching this block again.
 */

import * as React from "react";
import { DataTable, type DataTableColumn } from "./data-table";
import type { EmptyStateProps } from "./empty-state";
import { IntentBadge } from "./status-badge";
import type { Intent } from "../tokens";
import { formatRelativeTime } from "../format";

export interface CommitmentTableRow {
  uid: string;
  kind: string;
  title: string;
  state: string;
  /** Pre-resolved owner display name (never a raw actor uid). */
  ownerLabel?: string | null;
  targetOutcome?: string | null;
  fulfilledAt?: string | null;
  updatedAt: string;
}

/** Map a free-form commitment lifecycle state onto a badge intent. */
export function commitmentStateIntent(state: string): Intent {
  const s = state.toLowerCase();
  if (/(fulfill|done|complete|won|deliver)/.test(s)) return "success";
  if (/(risk|stall|block|overdue|late)/.test(s)) return "warning";
  if (/(cancel|abandon|lost|breach|fail)/.test(s)) return "danger";
  if (/(open|active|progress|deliver|commit)/.test(s)) return "info";
  return "neutral";
}

const stateLabel = (state: string) =>
  state.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

export interface CommitmentTableProps {
  rows: CommitmentTableRow[];
  onRowClick?: (row: CommitmentTableRow) => void;
  /** Override the empty line; defaults speak the canonical term. */
  empty?: React.ReactNode;
  /**
   * Rich blank state (icon + heading + guidance + a "do this next" action),
   * forwarded to the underlying `DataTable`. Takes precedence over `empty` —
   * reach for this instead of a bare string when the list can be legitimately
   * empty and the user needs a next step (e.g. the mode Objects view).
   */
  emptyState?: Omit<EmptyStateProps, "variant" | "colSpan">;
  /**
   * The C15-grounded label for the Commitment primitive — what the column
   * header reads ("Matters" for a law firm). Defaults to the canonical
   * "Commitment". The CALLER resolves this through `vocab()`/`useVocab()` (the
   * block takes it as a prop so it stays a server-safe presentation surface and
   * the canonical default keeps standard workspaces unchanged).
   */
  kindLabel?: string;
  wrapInCard?: boolean;
  className?: string;
  /**
   * Make the column headers sortable (Title · State · Owner · Updated) by
   * attaching comparators and routing the table to the interactive engine.
   * Default off keeps server-rendered mounts on the static path — turn it on
   * from a client boundary (e.g. the mode Objects view) where a list can grow
   * to hundreds of rows. Defaults the initial sort to newest-updated-first,
   * matching the server's `ORDER BY updated_at DESC`.
   */
  sortable?: boolean;
  /**
   * Paginate client-side at this page size (prev/next strip). Pair with
   * `sortable` for the standard big-list treatment when all rows are already
   * loaded. For genuinely unbounded API lists, server-paginate the endpoint and
   * drive a `<TablePager>` instead (see `/api/commitments`).
   */
  pageSize?: number;
}

export function CommitmentTable({
  rows,
  onRowClick,
  empty = "No commitments in flight.",
  emptyState,
  kindLabel = "Commitment",
  wrapInCard,
  className,
  sortable,
  pageSize,
}: CommitmentTableProps) {
  // Nulls last in either direction is the least-surprising owner/outcome order;
  // a missing label always trails a present one.
  const cmpText = (a?: string | null, b?: string | null) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return a.localeCompare(b);
  };
  const columns: DataTableColumn<CommitmentTableRow>[] = [
    {
      key: "title",
      header: kindLabel,
      sort: sortable ? (a, b) => a.title.localeCompare(b.title) : undefined,
      render: (row) => (
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate font-medium">{row.title}</span>
          <span className="type-subtext text-muted-foreground">
            {stateLabel(row.kind)}
          </span>
        </span>
      ),
    },
    {
      key: "state",
      header: "State",
      width: "w-32",
      sort: sortable ? (a, b) => a.state.localeCompare(b.state) : undefined,
      render: (row) => (
        <IntentBadge intent={commitmentStateIntent(row.state)} withDot>
          {stateLabel(row.state)}
        </IntentBadge>
      ),
    },
    {
      key: "owner",
      header: "Owner",
      width: "w-40",
      sort: sortable ? (a, b) => cmpText(a.ownerLabel, b.ownerLabel) : undefined,
      render: (row) =>
        row.ownerLabel ? (
          <span className="truncate">{row.ownerLabel}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: "outcome",
      header: "Target outcome",
      render: (row) =>
        row.targetOutcome ? (
          <span className="truncate text-muted-foreground">
            {row.targetOutcome}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: "updated",
      header: "Updated",
      width: "w-28",
      // SQLite datetime strings sort lexically == chronologically, so a string
      // compare is a correct recency sort (no Date parse needed).
      sort: sortable ? (a, b) => a.updatedAt.localeCompare(b.updatedAt) : undefined,
      defaultDesc: true,
      render: (row) => (
        <span className="text-muted-foreground">
          {formatRelativeTime(row.updatedAt)}
        </span>
      ),
    },
  ];

  return (
    <DataTable
      rows={rows}
      columns={columns}
      rowKey={(row) => row.uid}
      // Each row is a Play Dive-in `#focus-<uid>` scroll/highlight anchor (§2.6).
      rowId={(row) => `focus-${row.uid}`}
      onRowClick={onRowClick}
      empty={empty}
      emptyState={emptyState}
      wrapInCard={wrapInCard}
      className={className}
      // Opt-in big-list treatment: default newest-updated-first (matches the
      // server query) so the first sortable render matches the static order.
      initialSort={sortable ? { key: "updated", dir: "desc" } : undefined}
      pageSize={pageSize}
    />
  );
}
